import React, { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Loader2, Info, Trophy, Target, BarChart3, CheckCircle2, AlertCircle,
} from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * CombinedTrackRecordSummary — Phase 5 read-only summary tab.
 *
 * Shows CSE and US accuracy / return headline numbers side-by-side
 * WITHOUT merging the two markets. Per the project's strict
 * separation mandate (LKR vs USD never combined), each side stays
 * in its own column, with its own benchmark, in its own currency
 * scale. This tab deliberately exposes ONLY headline KPIs — the full
 * tables live on their respective CSE / US tabs.
 */

const fmtPct = (v, opts = {}) => {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(opts.decimals ?? 2)}%`;
};

const fmtAccuracy = (v) => {
  if (v == null) return "—";
  return `${v.toFixed(1)}%`;
};

const returnClass = (v) => {
  if (v == null) return "text-muted-foreground";
  if (v > 0) return "text-[hsl(var(--success))]";
  if (v < 0) return "text-[hsl(var(--destructive))]";
  return "text-muted-foreground";
};

export const CombinedTrackRecordSummary = ({ cseSummary: cseFromParent, usSummary: usFromParent }) => {
  const [cse, setCse] = useState(cseFromParent || null);
  const [us, setUs] = useState(usFromParent || null);
  const [loading, setLoading] = useState(!(cseFromParent && usFromParent));
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cseFromParent) setCse(cseFromParent);
  }, [cseFromParent]);
  useEffect(() => {
    if (usFromParent) setUs(usFromParent);
  }, [usFromParent]);

  useEffect(() => {
    // If parent didn't preload either summary (e.g. user lands directly
    // on the Combined tab), fetch them in parallel.
    if (cseFromParent && usFromParent) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [cseRes, usRes] = await Promise.all([
          cseFromParent ? Promise.resolve({ summary: cseFromParent }) : apiGet("/track-record?status=all&limit=1"),
          usFromParent ? Promise.resolve(usFromParent) : apiGet("/us-track-record/summary"),
        ]);
        if (!alive) return;
        setCse(cseRes.summary || cseRes || null);
        setUs(usRes || null);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || e?.message || "Failed to load summary");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [cseFromParent, usFromParent]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading combined summary…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6 text-sm text-[hsl(var(--destructive))]">{error}</div>
    );
  }

  // Verdict row — does each side beat its benchmark?
  const cseBeats = (cse?.avg_excess_return_pct ?? null);
  const usBeats = (us?.avg_excess_return_pct ?? null);

  return (
    <div className="space-y-6" data-testid="combined-track-record-summary">
      <Alert
        className="border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/5"
        data-testid="combined-track-record-separation-notice"
      >
        <Info className="h-4 w-4 text-[hsl(var(--info))]" />
        <AlertDescription className="text-xs leading-relaxed">
          <span className="font-semibold">Strict market separation.</span> CSE figures are LKR-only
          vs ASPI. US figures are USD-only vs S&amp;P 500 (^GSPC). The two are never merged or
          currency-converted — they're shown side-by-side so you can read each market on its own terms.
        </AlertDescription>
      </Alert>

      {/* Two-column headline grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MarketCard
          title="🇱🇰 CSE (LKR vs ASPI)"
          summary={cse}
          benchmarkLabel="ASPI"
          excessKey="avg_excess_return_pct"
          accuracyKey="accuracy_vs_aspi_pct"
          benchmarkReturnKey="avg_aspi_return_pct"
          testid="combined-cse-card"
        />
        <MarketCard
          title="🇺🇸 US (USD vs S&P 500)"
          summary={us}
          benchmarkLabel="S&P 500"
          excessKey="avg_excess_return_pct"
          accuracyKey="accuracy_vs_sp500_pct"
          benchmarkReturnKey="avg_sp500_return_pct"
          testid="combined-us-card"
          dataMaturing={us?.data_maturing}
          launchDate={us?.launch_date}
          daysSinceLaunch={us?.days_since_launch}
        />
      </div>

      {/* Verdict line */}
      <Card className="border bg-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            Combined verdict
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <VerdictRow
            market="CSE"
            beats={cseBeats}
            benchmarkLabel="ASPI"
            n={cse?.total_signals}
          />
          <VerdictRow
            market="US"
            beats={usBeats}
            benchmarkLabel="S&P 500"
            n={us?.total_signals}
            dataMaturing={us?.data_maturing}
          />
        </CardContent>
      </Card>
    </div>
  );
};

const MarketCard = ({
  title, summary, benchmarkLabel, excessKey, accuracyKey, benchmarkReturnKey,
  testid, dataMaturing, launchDate, daysSinceLaunch,
}) => {
  const s = summary || {};
  return (
    <Card className="border bg-card" data-testid={testid}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {dataMaturing && (
          <div className="flex items-start gap-2 rounded-md border border-[hsl(var(--info))]/30 bg-[hsl(var(--info))]/5 p-2.5 text-[11px] leading-relaxed">
            <AlertCircle className="h-3.5 w-3.5 text-[hsl(var(--info))] shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold">Data maturing.</span>{" "}
              {launchDate && <>Started {new Date(launchDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" })}</>}
              {daysSinceLaunch != null && <> ({daysSinceLaunch} day{daysSinceLaunch === 1 ? "" : "s"} so far)</>}
              . Meaningful accuracy after 90+ days.
            </div>
          </div>
        )}
        <KpiRow
          icon={BarChart3}
          label="Tracked signals"
          value={`${s.total_signals ?? "—"}`}
          sub={`${s.active_signals ?? 0} active · ${s.ended_signals ?? 0} ended`}
        />
        <KpiRow
          icon={BarChart3}
          label="Avg. return (all)"
          value={fmtPct(s.avg_return_all_pct)}
          tone={s.avg_return_all_pct}
          sub={`${benchmarkLabel}: ${fmtPct(s[benchmarkReturnKey])}`}
        />
        <KpiRow
          icon={Target}
          label={`Excess return vs ${benchmarkLabel}`}
          value={fmtPct(s[excessKey])}
          tone={s[excessKey]}
          sub={(s[excessKey] ?? null) == null
            ? "No data yet"
            : s[excessKey] > 0
              ? `Beating ${benchmarkLabel}`
              : `Trailing ${benchmarkLabel}`}
        />
        <KpiRow
          icon={CheckCircle2}
          label="Accuracy (price up)"
          value={fmtAccuracy(s.accuracy_price_up_pct)}
          sub={`Active: ${fmtAccuracy(s.accuracy_price_up_active_pct)}`}
        />
        <KpiRow
          icon={CheckCircle2}
          label={`Accuracy (vs ${benchmarkLabel})`}
          value={fmtAccuracy(s[accuracyKey])}
          sub={s.total_signals
            ? `${Math.round(((s[accuracyKey] ?? 0) / 100) * (s.total_signals || 0))} of ${s.total_signals} signals beat`
            : "—"}
        />
      </CardContent>
    </Card>
  );
};

const KpiRow = ({ icon: Icon, label, value, tone, sub }) => {
  const toneClass = tone == null ? "" : tone > 0 ? "text-[hsl(var(--success))]" : tone < 0 ? "text-[hsl(var(--destructive))]" : "";
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {Icon && <Icon className="h-3.5 w-3.5" />}
        {label}
      </div>
      <div className="text-right">
        <div className={`font-display text-base font-semibold tabular-nums ${toneClass}`}>{value}</div>
        {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );
};

const VerdictRow = ({ market, beats, benchmarkLabel, n, dataMaturing }) => {
  if (!n) {
    return (
      <div className="text-muted-foreground">
        {market}: <span className="font-mono">—</span> no signals tracked yet
      </div>
    );
  }
  if (dataMaturing) {
    return (
      <div className={`flex items-center gap-2 ${returnClass(beats)}`}>
        <span className="font-semibold">{market}:</span>
        <span className="text-foreground">
          {beats != null ? `${fmtPct(beats)} vs ${benchmarkLabel}` : "—"}
        </span>
        <span className="text-[10px] text-muted-foreground">
          (preliminary — data still maturing)
        </span>
      </div>
    );
  }
  if (beats == null) {
    return (
      <div className="text-muted-foreground">
        {market}: not enough closed/active data to evaluate vs {benchmarkLabel}
      </div>
    );
  }
  return (
    <div className={`flex items-center gap-2 ${returnClass(beats)}`}>
      <span className="font-semibold">{market}:</span>
      <span>
        {beats > 0 ? "Beating" : "Trailing"} {benchmarkLabel} by {fmtPct(Math.abs(beats))}
      </span>
      <span className="text-[10px] text-muted-foreground">
        ({n} signal{n === 1 ? "" : "s"})
      </span>
    </div>
  );
};
